// Tiny i18n accessor for the QR Phone caller flow and staff softphone.
//
// The whole app imports `t` from here and reads strings as `t.<key>` (calling the
// function-valued ones, e.g. `t.queuePosition(3)`). The language is resolved ONCE,
// when this module first loads — i.e. the language the window was launched with:
//
//   1. The language a kiosk launched the caller with (src/launchContext.ts): the kiosk's
//      own language, English when the caller does not have it; never persisted, so the
//      next guest of the device starts from the kiosk again; else
//   2. An explicit choice persisted by the language switch (localStorage), else
//   3. `navigator.language` starting with "es" → Spanish, else
//   4. English.
//
// English staying the default for every non-Spanish locale is load-bearing: the e2e
// suite extracts its expected strings from en.ts (e2e/get_strings.cjs) and runs the
// browser under en-US, so it keeps matching the rendered UI without changes.
//
// Because every consumer imports `t` as a module-level constant, changing language
// requires a reload — setLanguage() persists the choice and reloads deliberately
// instead of pretending to re-render live (see LanguageSwitch.tsx).
//
// Adding another language: copy en.ts, translate the values (keys and function
// signatures identical), register it in `dictionaries` and `BACKEND_LANGUAGE_CODES`,
// and extend the `Language` union plus `isLanguage`.
import { en } from './en'
import { es } from './es'
import type { Dictionary } from './en'
import { launchContext } from '../launchContext'

export type { Dictionary } from './en'

export type Language = 'en' | 'es'

const dictionaries: Record<Language, Dictionary> = {
  en,
  es,
}

// Backend message language codes (the API localises some responses by this code).
// Keep them next to the dictionaries so a language is configured in ONE place.
// Codes are defined by the backend: 1 = English, 3 = Spanish.
export const BACKEND_LANGUAGE_CODES: Record<Language, number> = {
  en: 1,
  es: 3,
}

// Persisted explicit choice; same `qr_` storage prefix as staffAuth's keys.
const LANGUAGE_KEY = 'qr_language'

const isLanguage = (value: unknown): value is Language => value === 'en' || value === 'es'

const readStoredLanguage = (): Language | null => {
  try {
    const stored = localStorage.getItem(LANGUAGE_KEY)
    return isLanguage(stored) ? stored : null
  } catch {
    return null // storage unavailable (private mode / embedded webview): fall through
  }
}

const detectLanguage = (): Language => {
  if (launchContext.lang) return isLanguage(launchContext.lang) ? launchContext.lang : 'en'
  const stored = readStoredLanguage()
  if (stored) return stored
  const locale = typeof navigator !== 'undefined' ? navigator.language || '' : ''
  return locale.toLowerCase().startsWith('es') ? 'es' : 'en'
}

// The active language, resolved once at module load (= at window launch).
export const currentLanguage: Language = detectLanguage()

// The active dictionary, consumed across the app as `t.<key>`.
export const t: Dictionary = dictionaries[currentLanguage]

// The active backend language code, consumed by the core HTTP layer / globals so
// backend-localised messages arrive in the same language as the UI.
export const backendLanguageCode: number = BACKEND_LANGUAGE_CODES[currentLanguage]

// Persist an explicit language choice and relaunch with it. The reload is intentional:
// strings are bound at module load, so this is the one honest way to apply the change
// everywhere (including the signaling core's status texts) at a screen boundary.
export const setLanguage = (language: Language): void => {
  if (language === currentLanguage) return
  try {
    localStorage.setItem(LANGUAGE_KEY, language)
  } catch {
    return // nowhere to persist: keep the current language rather than reload in vain
  }
  window.location.reload()
}
